import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";

export function Container({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("mx-auto w-full max-w-6xl px-5 sm:px-8", className)}>
      {children}
    </div>
  );
}

export function Eyebrow({
  children,
  tone = "dark",
}: {
  children: React.ReactNode;
  tone?: "dark" | "light";
}) {
  return (
    <p
      className={cn(
        "mb-4 text-base font-semibold tracking-[0.14em] uppercase",
        tone === "light" ? "text-brand-200" : "text-brand-600",
      )}
    >
      {children}
    </p>
  );
}

export function SectionTitle({
  eyebrow,
  title,
  intro,
  className,
  id,
  tone = "dark",
}: {
  eyebrow?: string;
  title: React.ReactNode;
  intro?: React.ReactNode;
  className?: string;
  id?: string;
  tone?: "dark" | "light";
}) {
  const light = tone === "light";
  return (
    <div className={cn("max-w-3xl", className)}>
      {eyebrow && <Eyebrow tone={tone}>{eyebrow}</Eyebrow>}
      <h2
        id={id}
        className={cn(
          "text-[2.1rem] font-semibold sm:text-[2.8rem]",
          light ? "text-white" : "text-brand-950",
        )}
      >
        {title}
      </h2>
      {intro && (
        <p className={cn("mt-5 text-xl", light ? "text-brand-100" : "text-ink-soft")}>
          {intro}
        </p>
      )}
    </div>
  );
}

const buttonBase =
  "inline-flex min-h-14 cursor-pointer items-center justify-center gap-3 rounded-full px-8 text-lg font-semibold whitespace-nowrap transition-[background-color,box-shadow,transform] duration-200 active:scale-[0.98]";

// Uma só ação principal no site inteiro: sempre o elemento de maior contraste.
export const buttonStyles = {
  primary: cn(
    buttonBase,
    "bg-brand-950 text-white shadow-soft hover:bg-brand-800 hover:shadow-lift",
  ),
  secondary: cn(
    buttonBase,
    "border-2 border-brand-200 bg-transparent text-brand-950 hover:border-brand-950",
  ),
  light: cn(buttonBase, "bg-white text-brand-950 shadow-lift hover:bg-brand-50"),
  ghostLight: cn(
    buttonBase,
    "border-2 border-white/30 bg-transparent text-white hover:border-white",
  ),
};

export function CtaButton({
  href,
  children,
  variant = "primary",
  className,
}: {
  href: string;
  children: React.ReactNode;
  variant?: keyof typeof buttonStyles;
  className?: string;
}) {
  return (
    <Link href={href} className={cn(buttonStyles[variant], className)}>
      {children}
      <ArrowRight aria-hidden className="size-5" />
    </Link>
  );
}
